// src/theme.js
// ============================================================================
// GVA Time Tracker — Dual-mode MUI Theme (light ↔ dark)
// ----------------------------------------------------------------------------
// Use getTheme(mode) to get the correct theme for the current mode.
// All color tokens are defined per-mode so no component needs hardcoded hex.
// The Sidebar Drawer stays dark in both modes by design.
// ============================================================================

import { createTheme } from '@mui/material/styles';
import { PHONE_MEDIA } from './hooks/useIsPhone';

const FONT_DISPLAY = `'Special Gothic Expanded One', 'Inter', 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', sans-serif`;
const FONT_BODY    = `'Inter', 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'ヒラギノ角ゴ ProN W3', 游ゴシック, sans-serif`;
const FONT_MONO    = `'JetBrains Mono', 'SF Mono', Consolas, monospace`;

// Brand navy ink. Light mode uses it for text/buttons/borders and the sidebar;
// dark mode builds its surfaces from deeper navy shades and keeps the sidebar
// in the brand navy.
const INK          = '#201c4d';
const NAVY_DEEP    = '#15132e';  // dark-mode page background
const NAVY_SOFT    = '#1d1a42';  // dark-mode soft surfaces
const ACCENT       = '#DE3163';
const ACCENT_DARK  = '#a62049';

const LIGHT = {
  bg:            '#ffffff',
  bgSoft:        '#f7f6f3',
  bgHover:       'rgba(0,0,0,0.04)',
  textPrimary:   INK,
  textSecondary: '#4a4a4a',
  textFaint:     '#8c8c8c',
  border:        'rgba(0,0,0,0.08)',
  borderStrong:  'rgba(0,0,0,0.18)',
  borderBlack:   INK,
  primaryMain:   INK,
  warnRowBg:     '#fffbe6',
  warnBar:       '#f0ca4d',
  success:       '#0f7b6c',
  error:         '#c23030',
  // Neutral chip surface. A `default` chip must NOT fall back to primaryMain
  // (navy ink) in light mode — a solid black box next to a green/amber status
  // chip reads as a state of its own rather than as "no state".
  chipBg:        '#eceae5',
  chipBgHover:   '#e0ddd6',
  chipText:      '#3a3a3a',
  chipBorder:    'rgba(0,0,0,0.12)',
  iconHoverBg:   'rgba(0,0,0,0.08)',
  sidebarBg:     INK,
};

const DARK = {
  bg:            NAVY_DEEP,
  bgSoft:        NAVY_SOFT,
  bgHover:       'rgba(255,255,255,0.06)',
  textPrimary:   '#ffffff',
  textSecondary: 'rgba(255,255,255,0.65)',
  textFaint:     'rgba(255,255,255,0.38)',
  border:        'rgba(255,255,255,0.10)',
  borderStrong:  'rgba(255,255,255,0.22)',
  borderBlack:   'rgba(255,255,255,0.85)',
  primaryMain:   '#ffffff',
  sidebarBg:     INK,
  warnRowBg:     '#2d2600',
  warnBar:       '#f0ca4d',
  success:       '#4caf50',
  error:         '#ef5350',
  // Dark mode keeps the inverted "ink" chip it has always had.
  chipBg:        '#ffffff',
  chipBgHover:   '#e0e0e0',
  chipText:      INK,
  chipBorder:    'rgba(255,255,255,0.85)',
  iconHoverBg:   'rgba(255,255,255,0.14)',
};

export const getTheme = (mode = 'light') => {
  const T = mode === 'light' ? LIGHT : DARK;
  const SIDEBAR_BG = T.sidebarBg;

  return createTheme({
    palette: {
      mode,
      primary: {
        main:         T.primaryMain,
        light:        mode === 'light' ? '#4d4a71' : 'rgba(255,255,255,0.85)',
        dark:         mode === 'light' ? '#16133a' : '#cccccc',
        contrastText: mode === 'light' ? '#ffffff' : INK,
      },
      secondary: {
        main:         ACCENT,
        light:        '#ff5b8b',
        dark:         ACCENT_DARK,
        contrastText: '#ffffff',
      },
      background: {
        default: T.bg,
        paper:   T.bg,
      },
      text: {
        primary:   T.textPrimary,
        secondary: T.textSecondary,
        disabled:  T.textFaint,
      },
      divider: T.border,
      error:   { main: T.error },
      warning: { main: T.warnBar },
      success: { main: T.success },
      // Custom editorial tokens — access via theme.palette.editorial.*
      editorial: {
        sidebarBg:    SIDEBAR_BG,
        borderStrong: T.borderStrong,
        borderBlack:  T.borderBlack,
        warnRowBg:    T.warnRowBg,
        warnBar:      T.warnBar,
        bgSoft:       T.bgSoft,
        bgHover:      T.bgHover,
        textFaint:    T.textFaint,
        textSecondary: T.textSecondary,
        chipBg:       T.chipBg,
        chipBgHover:  T.chipBgHover,
        chipText:     T.chipText,
        chipBorder:   T.chipBorder,
        iconHoverBg:  T.iconHoverBg,
      },
    },

    typography: {
      fontFamily:        FONT_BODY,
      fontFamilyDisplay: FONT_DISPLAY,
      fontFamilyMono:    FONT_MONO,
      h1: {
        fontFamily:    FONT_DISPLAY,
        fontWeight:    400,
        fontSize:      '3.5rem',
        lineHeight:    1.0,
        letterSpacing: '-0.01em',
        color:         T.textPrimary,
        // Page titles: 56px leaves no room beside a header button on a phone.
        // Matches breakpoints.down('sm').
        '@media (max-width:599.95px)': { fontSize: '1.75rem' },
      },
      h2: {
        fontFamily:    FONT_DISPLAY,
        fontWeight:    400,
        fontSize:      '1.75rem',
        lineHeight:    1.2,
        letterSpacing: '-0.01em',
      },
      h3: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.3 },
      h4: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem',     lineHeight: 1.4 },
      h5: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.9375rem' },
      h6: { fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: '1.25rem', letterSpacing: '-0.01em' },
      body1:   { fontSize: '0.875rem',  lineHeight: 1.5 },
      body2:   { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.71875rem', color: T.textSecondary, fontFamily: FONT_MONO },
      overline: {
        fontSize:      '0.6875rem',
        fontWeight:    600,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color:         T.textFaint,
        lineHeight:    1.4,
      },
      button: { fontWeight: 500, textTransform: 'none' },
    },

    shape: { borderRadius: 8 },

    shadows: [
      'none',
      '0 1px 2px rgba(0,0,0,0.06)',
      '0 2px 4px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)',
      '0 4px 12px rgba(0,0,0,0.09), 0 2px 4px rgba(0,0,0,0.06)',
      '0 6px 14px rgba(0,0,0,0.09)',
      '0 8px 18px rgba(0,0,0,0.10)',
      '0 10px 22px rgba(0,0,0,0.10)',
      '0 12px 24px rgba(0,0,0,0.11)',
      '0 14px 28px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.07)',
      ...Array(16).fill('0 14px 28px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.07)'),
    ],

    components: {
      // ── Buttons ──────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius:  999,
            textTransform: 'none',
            fontWeight:    500,
            fontSize:      '0.8125rem',
            padding:       '6px 16px',
            minHeight:     32,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: ({ theme }) => ({
            borderColor: theme.palette.editorial.borderStrong,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              background:  theme.palette.editorial.bgHover,
            },
          }),
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            width:        28,
            height:       28,
            borderRadius: '50%',
            color:        theme.palette.text.secondary,
            // Neutral tint, not a solid primary.main disc — a row-action icon
            // should not turn into a black box on hover in light mode.
            '&:hover': {
              background: theme.palette.editorial.iconHoverBg,
              color:      theme.palette.text.primary,
            },
          }),
        },
      },

      // ── Form fields ──────────────────────────────────────────────────────
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
              background:   theme.palette.background.paper,
              '& fieldset':             { borderColor: theme.palette.editorial.borderStrong },
              '&:hover fieldset':       { borderColor: theme.palette.primary.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1 },
              // A pill only reads as intentional on a single-line control. The
              // browser clamps a 999px radius to half the height, so a multiline
              // field (email body, notes) turns into a stadium whose curve cuts
              // across the first and last lines of text. Multiline gets the
              // standard 8px corner instead — see MuiOutlinedInput below, which
              // carries the same rule for inputs built without TextField.
              '&.MuiInputBase-multiline': { borderRadius: 8 },
            },
            '& .MuiInputBase-input': { fontSize: '0.8125rem', padding: '6px 14px' },
            '& .MuiInputLabel-root': {
              fontSize:      '0.6875rem',
              fontWeight:    600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color:         theme.palette.editorial.textFaint,
            },
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              cursor: 'pointer',
              filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
            },
            '& input[type="month"]::-webkit-calendar-picker-indicator': {
              cursor: 'pointer',
              filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
            },
          }),
        },
      },

      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          outlined: { borderRadius: 999, fontSize: '0.8125rem' },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 999,
            backgroundColor: theme.palette.background.paper,
            '& fieldset':             { borderColor: theme.palette.editorial.borderStrong },
            '&:hover fieldset':       { borderColor: theme.palette.primary.main },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1 },
            // Same reason as in MuiTextField above: the pill is for single-line
            // controls only; multiline drops to the standard 8px corner.
            '&.MuiInputBase-multiline': { borderRadius: 8 },
          }),
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize:      '0.6875rem',
            fontWeight:    600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color:         theme.palette.editorial.textFaint,
          }),
        },
      },

      // ── Surfaces ─────────────────────────────────────────────────────────
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root:     ({ theme }) => ({ backgroundColor: theme.palette.background.paper, backgroundImage: 'none' }),
          outlined: ({ theme }) => ({ borderColor: theme.palette.divider }),
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0, variant: 'outlined' },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius:    12,
            borderColor:     theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },

      MuiDivider: {
        styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
      },

      // ── Drawer / Sidebar — always dark ───────────────────────────────────
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: SIDEBAR_BG,
            backgroundImage: 'none',
            borderRight:     'none',
            color:           '#ffffff',
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            color:        'rgba(255,255,255,0.75)',
            '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.75)', minWidth: 28 },
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.06)',
              color:           '#ffffff',
              '& .MuiListItemIcon-root': { color: '#ffffff' },
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.14)'
                : '#ffffff',
              color: theme.palette.mode === 'dark' ? '#ffffff' : SIDEBAR_BG,
              fontWeight: 600,
              '& .MuiListItemIcon-root': {
                color: theme.palette.mode === 'dark' ? '#ffffff' : SIDEBAR_BG,
              },
            },
          }),
        },
      },

      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: '13.5px', fontWeight: 500 },
        },
      },

      // ── Tables ───────────────────────────────────────────────────────────
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontSize:      '0.6875rem',
            fontWeight:    700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color:         theme.palette.text.primary,
            background:    theme.palette.background.paper,
            borderBottom:  `2px solid ${theme.palette.editorial.borderBlack}`,
          }),
          body: ({ theme }) => ({
            borderColor: theme.palette.divider,
            padding:     '14px 12px',
            color:       theme.palette.text.primary,
          }),
        },
      },

      MuiDataGrid: {
        defaultProps: {
          columnHeaderHeight: 52,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            border:          `1px solid ${theme.palette.divider}`,
            borderRadius:    8,
            backgroundColor: theme.palette.background.paper,
            fontSize:        '0.8125rem',
            fontFamily:      FONT_BODY,
            color:           theme.palette.text.primary,
            '--DataGrid-rowBorderColor': theme.palette.divider,
            // Hide menu icon and sort button by default; reveal on column header hover
            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': { visibility: 'hidden' },
            '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': { visibility: 'visible !important' },
            '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': { visibility: 'hidden', width: 0 },
            // Show sort button on hover OR when column is actively sorted
            '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer': { visibility: 'visible !important', width: 'auto !important' },
            '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer': { visibility: 'visible !important', width: 'auto !important' },
          }),
          columnHeaders: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            borderBottom:    `2px solid ${theme.palette.editorial.borderBlack}`,
          }),
          columnHeader: { padding: '6px 12px' },
          columnHeaderTitle: ({ theme }) => ({
            fontSize:      '0.6875rem',
            fontWeight:    700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color:         theme.palette.text.primary,
            whiteSpace:    'normal',
            lineHeight:    1.3,
            overflow:      'visible',
            textOverflow:  'clip',
          }),
          cell: ({ theme }) => ({
            borderColor: theme.palette.divider,
            padding:     '14px 12px',
            color:       theme.palette.text.primary,
          }),
          row: ({ theme }) => ({
            '&:hover': { backgroundColor: theme.palette.editorial.bgHover },
          }),
        },
      },

      // ── Chips ─────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius:  2,
            fontSize:      '0.625rem',
            fontWeight:    700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            height:        20,
          },
          // NOTE: theme styleOverrides are emitted AFTER MUI's own colour
          // variants, so an unconditional `filled` / `outlined` rule wins over
          // colorSuccess / colorWarning / colorSecondary and flattens every
          // status chip in the app to one colour. Both slots are therefore
          // scoped to the *default* colour and return {} for the rest, so
          // `color="success"` etc. keep their real palette colour.
          filled: ({ theme, ownerState }) => (
            (ownerState.color && ownerState.color !== 'default')
              ? {}
              : {
                  backgroundColor: theme.palette.editorial.chipBg,
                  color:           theme.palette.editorial.chipText,
                  border:          `1px solid ${theme.palette.editorial.chipBorder}`,
                  '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                    backgroundColor: theme.palette.editorial.chipBgHover,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.editorial.chipText,
                  },
                }
          ),
          outlined: ({ theme, ownerState }) => ({
            fontFamily:    FONT_MONO,
            letterSpacing: '0.12em',
            ...((ownerState.color && ownerState.color !== 'default')
              ? {}
              : {
                  borderColor: theme.palette.editorial.borderBlack,
                  color:       theme.palette.text.primary,
                }),
          }),
        },
      },

      // ── Feedback ─────────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 2, backgroundColor: 'transparent' },
          bar:  { backgroundColor: ACCENT },
        },
      },

      MuiCircularProgress: {
        defaultProps: { color: 'primary' },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: SIDEBAR_BG,
            color:           '#ffffff',
            fontSize:        '0.75rem',
            fontWeight:      500,
            padding:         '6px 10px',
            borderRadius:    6,
          },
          arrow: { color: SIDEBAR_BG },
        },
      },

      // Phones: iOS Safari zooms the whole page into any text input under 16px when it
      // is tapped. !important because MuiTextField sets the size on a nested selector.
      MuiCssBaseline: {
        styleOverrides: {
          [PHONE_MEDIA]: {
            'input.MuiInputBase-input, textarea.MuiInputBase-input': { fontSize: '16px !important' },
          },
        },
      },

      // ── Dialogs ───────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius:    12,
            border:          `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            // Phones: whatever a dialog asks for, it fits the screen. Full-screen
            // dialogs set their own geometry; small confirms keep their size but lose
            // MUI's 32px margin, which leaves almost no width on a small phone.
            [PHONE_MEDIA]: {
              '&:not(.MuiDialog-paperFullScreen)': {
                margin: 8,
                width: 'calc(100% - 16px)',
                maxWidth: 'calc(100% - 16px)',
                maxHeight: 'calc(100dvh - 16px)',
              },
              '&.MuiDialog-paperFullScreen': { borderRadius: 0, border: 'none' },
            },
          }),
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily:    FONT_DISPLAY,
            fontSize:      '1.5rem',
            fontWeight:    400,
            letterSpacing: '-0.01em',
            padding:       '24px 24px 8px',
            [PHONE_MEDIA]: { fontSize: '1.25rem', padding: '16px 16px 8px', overflowWrap: 'anywhere' },
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: { [PHONE_MEDIA]: { padding: '12px 16px' } },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          // Phones: a row of long-labelled buttons wraps instead of running off the edge.
          root: { [PHONE_MEDIA]: { flexWrap: 'wrap', rowGap: 8, padding: '8px 16px 12px' } },
        },
      },

      // ── Menu ─────────────────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          root: {
            '& .MuiMenu-list': { display: 'flex', flexDirection: 'column' },
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: { display: 'block', width: '100%' },
        },
      },

      // ── Checkbox ─────────────────────────────────────────────────────────
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-checked': { color: ACCENT },
          }),
        },
      },

      // ── AppBar ────────────────────────────────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color:           theme.palette.text.primary,
            boxShadow:       `0 1px 0 ${theme.palette.divider}`,
          }),
        },
      },
    },
  });
};

export default getTheme('light');
